import React, { useEffect, useState } from 'react';

/**
 * IframeTestPage - Test page to see how GeoAutomationViewer performs in an iframe
 * 
 * Features:
 * - Displays scene image
 * - "View in 3D" button overlays iframe with proper coordinates
 * - Receives camera coordinates when "Back to 2D" is clicked
 * 
 * Navigate to: /iframe-test
 */

interface CameraData {
  gpsPosition: { 
    latitude: number; 
    longitude: number; 
    altitude: number;
    heading?: number;    // Yaw - compass direction (0-360°)
    pitch?: number;      // Looking up/down (-90 to +90°)
    roll?: number;       // Camera tilt
  } | null;
  sceneId?: string;
  error?: string;
}

type SceneType = 'terrebonne' | 'officespace';

const SCENES = {
  terrebonne: {
    name: 'Terrebonne Municipality',
    image: '/terrebonne-2d.jpg',
    params: {
      scene: 'terrebonne',
      lat: 45.711716715372326,
      lon: -73.63830042963465,
      alt: 58.856383377944255,
      heading: 0
    }
  },
  officespace: {
    name: 'Office Space',
    image: '/OfficeSpace.jpg',
    params: {
      scene: 'officespace',
      lat: 45.5017,
      lon: -73.5673,
      alt: 2,
      heading: 0
    }
  }
};

const IframeTestPage: React.FC = () => {
  const [showIframe, setShowIframe] = useState(false);
  const [lastCoordinates, setLastCoordinates] = useState<CameraData | null>(null);
  const [currentScene, setCurrentScene] = useState<SceneType>('terrebonne');

  // Lock parent page scroll when component mounts
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHeight = document.body.style.height;
    
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.height = originalHeight;
    };
  }, []);

  // Handle ESC key to close iframe
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showIframe) {
        setShowIframe(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showIframe]);

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'BACK_TO_2D') {
        console.log('[IframeTest] Received BACK_TO_2D message:', event.data);
        
        if (event.data.data) {
          setLastCoordinates(event.data.data);
          
          if (event.data.data.gpsPosition) {
            console.log('[IframeTest] ===== GPS POSITION =====');
            console.log('[IframeTest] Latitude:', event.data.data.gpsPosition.latitude);
            console.log('[IframeTest] Longitude:', event.data.data.gpsPosition.longitude);
            console.log('[IframeTest] Altitude:', event.data.data.gpsPosition.altitude);
            console.log('[IframeTest] Heading:', event.data.data.gpsPosition.heading);
            console.log('[IframeTest] Pitch:', event.data.data.gpsPosition.pitch);
            console.log('[IframeTest] Roll:', event.data.data.gpsPosition.roll);
            console.log('[IframeTest] ============================');
          } else {
            console.warn('[IframeTest] No GPS position in response');
          }
          
          console.log('[IframeTest] Scene ID:', event.data.data.sceneId);
          
          if (event.data.data.error) {
            console.error('[IframeTest] Error from viewer:', event.data.data.error);
          }
        }
        
        setShowIframe(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const scene = SCENES[currentScene];
  const iframeUrl = `/geoAutomationViewer?scene=${scene.params.scene}&lat=${scene.params.lat}&lon=${scene.params.lon}&alt=${scene.params.alt}&heading=${scene.params.heading}`;

  return (
    <div className="fixed inset-0 bg-gray-900 overflow-hidden">
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="bg-gray-800 border-b border-gray-700 p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">
                GeoAutomation Integration Test
              </h1>
              <p className="text-gray-300 text-sm mt-1">
                {scene.name} - Click "View in 3D" to load the interactive viewer
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Scene Selector */}
              <select
                value={currentScene}
                onChange={(e) => {
                  setCurrentScene(e.target.value as SceneType);
                  setLastCoordinates(null);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all duration-200"
              >
                <option value="terrebonne">Terrebonne</option>
                <option value="officespace">Office Space</option>
              </select>

              {lastCoordinates && (
                <button
                  onClick={() => setLastCoordinates(null)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  <span>🔄</span>
                  Reset
                </button>
              )}
              
              <button
                onClick={() => setShowIframe(true)}
                className="px-6 py-3 bg-[#0CB4E4] hover:bg-[#0CB4E4]/90 text-black font-semibold rounded-lg shadow-lg transition-all duration-200 flex items-center gap-2"
              >
                <span className="text-xl">🎯</span>
                3D View
              </button>
            </div>
          </div>
        </div>

        {/* Image Container */}
        <div className="flex-1 relative overflow-hidden bg-black">
          <img 
            src={scene.image}
            alt={scene.name}
            className="w-full h-full object-contain"
          />
          
          {/* Overlay Info */}
          <div className="absolute bottom-8 left-8 bg-black/70 backdrop-blur-sm p-4 rounded-lg border border-white/20 max-w-md">
            <h2 className="text-2xl font-bold text-white mb-2">{scene.name}</h2>
            <p className="text-gray-300 mb-4">
              {lastCoordinates 
                ? '📍 Returned from 3D with coordinates!' 
                : 'Click "View in 3D" to explore this location'}
            </p>
            
            {/* Show GPS coordinates */}
            {lastCoordinates && lastCoordinates.gpsPosition && (
              <div className="mb-4 p-3 bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-lg">
                <h3 className="text-sm font-semibold text-[#0CB4E4] mb-2">
                  📍 GPS Coordinates (from 3D Viewer)
                </h3>
                <div className="text-xs text-gray-300 space-y-1">
                  <div className="font-semibold text-white mb-1">Location:</div>
                  <div className="ml-2">Latitude: <span className="text-white font-mono">{lastCoordinates.gpsPosition.latitude?.toFixed(8)}°</span></div>
                  <div className="ml-2">Longitude: <span className="text-white font-mono">{lastCoordinates.gpsPosition.longitude?.toFixed(8)}°</span></div>
                  <div className="ml-2">Altitude: <span className="text-white font-mono">{lastCoordinates.gpsPosition.altitude?.toFixed(2)} m</span></div>
                  
                  {/* Camera Orientation */}
                  {(lastCoordinates.gpsPosition.heading !== undefined || 
                    lastCoordinates.gpsPosition.pitch !== undefined || 
                    lastCoordinates.gpsPosition.roll !== undefined) && (
                    <>
                      <div className="font-semibold text-white mt-2 mb-1">Camera Orientation:</div>
                      {lastCoordinates.gpsPosition.heading !== undefined && (
                        <div className="ml-2">
                          Heading: <span className="text-white font-mono">{lastCoordinates.gpsPosition.heading?.toFixed(2)}°</span>
                          <span className="text-gray-500 ml-2 text-xs">
                            ({lastCoordinates.gpsPosition.heading < 45 || lastCoordinates.gpsPosition.heading > 315 ? 'N' :
                              lastCoordinates.gpsPosition.heading < 135 ? 'E' :
                              lastCoordinates.gpsPosition.heading < 225 ? 'S' : 'W'})
                          </span>
                        </div>
                      )}
                      {lastCoordinates.gpsPosition.pitch !== undefined && (
                        <div className="ml-2">Pitch: <span className="text-white font-mono">{lastCoordinates.gpsPosition.pitch?.toFixed(2)}°</span></div>
                      )}
                      {lastCoordinates.gpsPosition.roll !== undefined && (
                        <div className="ml-2">Roll: <span className="text-white font-mono">{lastCoordinates.gpsPosition.roll?.toFixed(2)}°</span></div>
                      )}
                    </>
                  )}
                  
                  {lastCoordinates.sceneId && (
                    <div className="mt-2">Scene: <span className="text-white font-mono">{lastCoordinates.sceneId}</span></div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 GeoAutomation can use these GPS coordinates to pan/zoom their 2D map
                </p>
              </div>
            )}
            
            {/* Show error if coordinates failed */}
            {lastCoordinates && lastCoordinates.error && (
              <div className="mb-4 p-3 bg-red-900/20 border border-red-600/30 rounded-lg">
                <h3 className="text-sm font-semibold text-red-400 mb-2">
                  ⚠️ Error Getting Coordinates
                </h3>
                <p className="text-xs text-gray-300">{lastCoordinates.error}</p>
              </div>
            )}

            <div className="text-xs text-gray-400 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[#0CB4E4]">•</span>
                <span>Navigate with arrow keys or WASD</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#0CB4E4]">•</span>
                <span>Click "Back to 2D" to return with position</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#0CB4E4]">•</span>
                <span>Use coordinates to sync 2D view</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Iframe Overlay */}
      {showIframe && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-4 bg-gray-900 rounded-lg overflow-hidden shadow-2xl">
            <iframe
              src={iframeUrl}
              className="w-full h-full"
              title="GeoAutomation 3D Viewer"
              allow="accelerometer; gyroscope; fullscreen"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default IframeTestPage;